import useApi from "@/hooks/useApi";
import * as React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Invitation, invitationTypeDefs } from "./types";
import { PrimaryBlueButton, SecondaryGrayButton, Modal, ModalHeader, ModalBody, ModalFooter } from "@velaro/velaro-component-lib";
import LoadingSpinner from "@/components/LoadingSpinner";
import InvitationModal from "./InvitationModal";
import { IconTrash } from "@velaro/velaro-component-lib";
import IconEditPencil2 from "@/icons/IconEditPencil2";
import useTeams from "@/hooks/useTeams";
import { getExpressionLabel } from "@/components/Expressions/types";
import Tooltip from "@/components/Tooltip";
import { Link } from "react-router-dom";

type TypeFilter = "all" | Invitation["type"];

const TYPE_ICONS: Record<Invitation["type"], React.ReactNode> = {
  bubble: (
    <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  ),
  popover: (
    <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="3" width="18" height="14" rx="2" />
      <circle cx="8" cy="10" r="2" />
      <line x1="13" y1="8" x2="19" y2="8" />
      <line x1="13" y1="12" x2="17" y2="12" />
    </svg>
  ),
  banner: (
    <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="1" y="4" width="22" height="6" rx="1" />
      <line x1="1" y1="14" x2="23" y2="14" />
      <line x1="4" y1="18" x2="20" y2="18" />
    </svg>
  ),
  inline: (
    <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="2" y="3" width="20" height="18" rx="2" />
      <line x1="7" y1="8" x2="17" y2="8" />
      <line x1="7" y1="12" x2="17" y2="12" />
      <line x1="7" y1="16" x2="13" y2="16" />
    </svg>
  ),
};

const TYPE_COLORS: Record<Invitation["type"], string> = {
  bubble: "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/20 dark:text-blue-300",
  popover: "bg-violet-50 text-violet-700 border-violet-200 dark:bg-violet-900/20 dark:text-violet-300",
  banner: "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-300",
  inline: "bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-300",
};

const TYPE_TABS: { value: TypeFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "bubble", label: "Bubble" },
  { value: "popover", label: "Popover" },
  { value: "banner", label: "Banner" },
  { value: "inline", label: "Inline" },
];

export default function Index() {
  const [invitations, setInvitations] = useState<Invitation[] | undefined>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<number | undefined>();
  const [invitationToDelete, setInvitationToDelete] = useState<number | null>(null);
  const [typeFilter, setTypeFilter] = useState<TypeFilter>("all");
  const { getTeamNameById } = useTeams();
  const api = useApi();

  useEffect(() => {
    async function loadInvitations() {
      try {
        const response = await api.messaging.get("Invitations/List");
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = (await response.json()) as Invitation[];
        setInvitations(Array.isArray(data) ? data : []);
      } catch (err) {
        setError(`Failed to load invitations: ${err instanceof Error ? err.message : String(err)}`);
        setInvitations([]);
      } finally {
        setLoading(false);
      }
    }
    loadInvitations();
  }, [api.messaging]);

  const remove = useCallback(
    async (id: number) => {
      setLoading(true);
      try {
        const response = await api.messaging.delete(`Invitations/${id}`);
        if (response.ok) {
          setInvitations((prev) => prev?.filter((i) => i.id !== id));
        }
      } catch (err) {
        setError(`Failed to delete: ${err instanceof Error ? err.message : String(err)}`);
      } finally {
        setLoading(false);
      }
    },
    [api.messaging]
  );

  const filtered = useMemo(() => {
    if (!invitations) return [];
    return typeFilter === "all" ? invitations : invitations.filter((i) => i.type === typeFilter);
  }, [invitations, typeFilter]);

  if (loading && !invitations) {
    return <LoadingSpinner />;
  }

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Page header */}
      <div className="flex items-start justify-between mb-5">
        <div className="flex-1">
          <h1 className="text-xl font-semibold text-slate-800">Chat Invitations</h1>
          <p className="text-sm text-slate-500 mt-0.5 max-w-xl">
            Invitations control <strong>how</strong> your chat widget appears to visitors — animated bubbles, popovers, banners, or inline embeds.
            Each invitation defines display rules (when to show) and visual style.
          </p>
        </div>
        <div className="shrink-0 ml-4">
          <PrimaryBlueButton
            onClick={() => setSelectedId(0)}
            label="+ New Invitation"
          />
        </div>
      </div>

      {/* Distinction callout */}
      <div className="mb-5 rounded-lg border border-sky-100 bg-sky-50 px-4 py-3 text-sm text-sky-900 flex gap-3 items-start">
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="shrink-0 mt-0.5 text-sky-500" aria-hidden="true">
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
        <span>
          <strong>Invitations vs Proactive Engagement:</strong>{" "}
          Invitations control the widget&apos;s visual appearance and display trigger on your page.{" "}
          <Link to="/proactive-engagement" className="underline font-medium">Proactive Engagement</Link>{" "}
          generates AI-driven contextual messages after the widget opens.
        </span>
      </div>

      {/* Type guide cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {invitationTypeDefs.map((def) => (
          <button
            key={def.value}
            type="button"
            onClick={() => setTypeFilter(def.value as TypeFilter)}
            className={`text-left rounded-lg border p-3 transition-all hover:shadow-sm ${
              typeFilter === def.value
                ? "border-primary bg-primary-bg shadow-sm"
                : "border-slate-200 bg-white hover:border-slate-300 dark:border-slate-700 dark:bg-slate-800"
            }`}
          >
            <div className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-xs font-medium mb-2 ${TYPE_COLORS[def.value as Invitation["type"]]}`}>
              {TYPE_ICONS[def.value as Invitation["type"]]}
              {def.label}
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">{def.description}</p>
            <p className="text-xs font-medium text-primary mt-1.5">
              {(invitations ?? []).filter((i) => i.type === def.value).length} configured
            </p>
          </button>
        ))}
      </div>

      {/* Type filter tabs */}
      <div className="flex items-center gap-1 mb-4 border-b border-slate-200 dark:border-slate-700">
        {TYPE_TABS.map((tab) => (
          <button
            key={tab.value}
            type="button"
            onClick={() => setTypeFilter(tab.value)}
            className={`pb-2 px-3 text-sm font-medium border-b-2 transition-colors ${
              typeFilter === tab.value
                ? "border-primary text-primary"
                : "border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400"
            }`}
          >
            {tab.label}
            {tab.value !== "all" && (invitations ?? []).filter((i) => i.type === tab.value).length > 0 && (
              <span className="ml-1 text-xs text-slate-400">
                ({(invitations ?? []).filter((i) => i.type === tab.value).length})
              </span>
            )}
          </button>
        ))}
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-2.5 text-sm text-red-700">{error}</div>
      )}

      {loading && (
        <div className="flex justify-center py-8"><LoadingSpinner inline /></div>
      )}

      {!loading && filtered.length === 0 && (
        <div className="text-center py-12 border border-dashed border-slate-300 dark:border-slate-600 rounded-lg">
          <div className="text-3xl mb-3">
            {typeFilter === "bubble" ? "💬" : typeFilter === "popover" ? "🪟" : typeFilter === "banner" ? "📢" : typeFilter === "inline" ? "⬜" : "💬"}
          </div>
          <p className="text-slate-500 dark:text-slate-400 mb-2">
            {typeFilter === "all" ? "No invitations yet" : `No ${typeFilter} invitations yet`}
          </p>
          <p className="text-sm text-slate-400 mb-4 max-w-xs mx-auto">
            {typeFilter === "all"
              ? "Create an invitation to control how your chat widget appears to visitors."
              : invitationTypeDefs.find((d) => d.value === typeFilter)?.description}
          </p>
          <PrimaryBlueButton onClick={() => setSelectedId(0)} label="+ Create Invitation" />
        </div>
      )}

      {!loading && filtered.length > 0 && (
        <div className="space-y-2">
          {filtered.map((invitation) => {
            const ruleLabel = getExpressionLabel(invitation.triggerExpression, getTeamNameById);
            const typeColor = TYPE_COLORS[invitation.type];
            const typeIcon = TYPE_ICONS[invitation.type];

            return (
              <div
                key={invitation.id}
                className="border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 px-4 py-3 flex items-center gap-4 hover:border-slate-300 dark:hover:border-slate-600 transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="font-medium text-sm truncate">{invitation.name}</span>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-medium shrink-0 ${typeColor}`}>
                      {typeIcon}
                      {invitation.type.charAt(0).toUpperCase() + invitation.type.slice(1)}
                    </span>
                  </div>
                  {invitation.description && (
                    <p className="text-xs text-slate-400 truncate mb-0.5">{invitation.description}</p>
                  )}
                  <div className="flex items-center gap-3 text-xs text-slate-400">
                    <span title="Trigger condition">
                      <i className="fa-solid fa-filter mr-1" />
                      {ruleLabel || "Always show"}
                    </span>
                    {invitation.resetFrequency && invitation.resetFrequency !== "Never" && (
                      <span title="Reset frequency">
                        <i className="fa-regular fa-clock mr-1" />
                        Resets {invitation.resetFrequency.toLowerCase()}
                      </span>
                    )}
                    {invitation.maxFires && invitation.maxFires > 0 && (
                      <span title="Max fires per visitor">
                        <i className="fa-solid fa-bolt mr-1" />
                        Max {invitation.maxFires}×
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Tooltip text="Edit" direction="top" minWidth="50px">
                    <button
                      type="button"
                      className="text-slate-400 hover:text-primary p-1"
                      onClick={() => setSelectedId(invitation.id)}
                    >
                      <IconEditPencil2 />
                    </button>
                  </Tooltip>
                  <Tooltip text="Delete" direction="top" minWidth="60px">
                    <button
                      type="button"
                      className="text-slate-400 hover:text-red-500 p-1"
                      onClick={() => setInvitationToDelete(invitation.id ?? null)}
                    >
                      <IconTrash />
                    </button>
                  </Tooltip>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {selectedId !== undefined && (
        <InvitationModal
          onClose={() => setSelectedId(undefined)}
          onUpdate={(invitation) => {
            setSelectedId(undefined);
            setInvitations((prev) => {
              if (!prev) return [invitation];
              const index = prev.findIndex((i) => i.id === invitation.id);
              if (index === -1) return [...prev, invitation];
              const clone = [...prev];
              clone[index] = invitation;
              return clone;
            });
          }}
          invitation={invitations?.find((i) => i.id === selectedId)}
        />
      )}

      <Modal show={invitationToDelete !== null}>
        <ModalHeader title="Delete Invitation" onClose={() => setInvitationToDelete(null)} />
        <ModalBody>
          <div className="m-4 text-sm text-slate-600">
            Are you sure you want to delete this invitation? Visitors will no longer see this widget on your site.
          </div>
        </ModalBody>
        <ModalFooter>
          <div className="mx-4 my-2 flex justify-between items-center">
            <SecondaryGrayButton label="Cancel" onClick={() => setInvitationToDelete(null)} />
            <PrimaryBlueButton
              label="Delete"
              onClick={() => {
                if (invitationToDelete !== null) {
                  remove(invitationToDelete);
                }
                setInvitationToDelete(null);
              }}
            />
          </div>
        </ModalFooter>
      </Modal>
    </div>
  );
}
